/**
 * PendingConnectionsScreen — completes the trusted-party async handshake
 * described in docs/KEY_WRAPPING_DESIGN.md §7.1 option A.
 *
 * Shows one row per (active successor or keyholder) where the invitee
 * accepted + published a wrapping public key but the primary hasn't yet
 * sealed their master key for that invitee. Tapping "Complete" pulls the
 * primary's master key from the keychain, seals it under the invitee's
 * published pubkey via TrustedPartyWrapping.sealForRecipient, and POSTs
 * the blob — after which the server clears the pending marker.
 *
 * If the primary's master key isn't cached (e.g., logged out + logged
 * back in on a new device without unlocking), we route them to
 * UnlockVaultScreen first.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  ActivityIndicator,
  Alert,
  SafeAreaView,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';

import { ms } from '../../utils/responsive';
import { createLogger } from '../../utils/logger';
import { navigationRef } from '../../services/navigationService';
import { vaultService } from '../../services/vaultService';
import {
  getCachedMasterKey,
  isWrappedMasterKeyEnrolled,
} from '../../services/encryption/MasterKeyService';
import { sealForRecipient } from '../../services/encryption/TrustedPartyWrapping';

const logger = createLogger('PendingConnections');

const SEAL_VERSION = 'tpw1' as const;

type PendingConnection = {
  relationshipId: string;
  trustedPartyUserId: string;
  trustedPartyEmail: string;
  trustedPartyFirstName: string | null;
  trustedPartyLastName: string | null;
  role: 'successor' | 'keyholder';
  wrappingPublicKey: string;
  createdAt: string;
};

export const PendingConnectionsScreen: React.FC = () => {
  const navigation = useNavigation<any>();
  const [pending, setPending] = useState<PendingConnection[] | null>(null);
  const [completingIds, setCompletingIds] = useState<Set<string>>(new Set());

  const navigateToRecoveryCode = () => {
    navigation.navigate('RecoveryCodeSettings');
  };

  const loadPending = useCallback(async (showErrorAlert = true) => {
    try {
      const list = await vaultService.listPendingTrustedPartyHandshakes();
      setPending(list);
    } catch (err) {
      logger.error('Failed to list pending connections:', err);
      setPending([]);
      if (!showErrorAlert) return;
      Alert.alert(
        'Could not load connections',
        'We could not load your pending connections. Please try again.',
      );
    }
  }, []);

  useEffect(() => {
    loadPending();
  }, [loadPending]);

  const handleComplete = async (row: PendingConnection) => {
    const enrolled = await isWrappedMasterKeyEnrolled();
    if (!enrolled) {
      Alert.alert(
        'Set up your Recovery Code first',
        'Complete your Recovery Code setup before connecting trusted parties.',
        [
          { text: 'Cancel', style: 'cancel' },
          {
            text: 'Go to Recovery Code',
            onPress: navigateToRecoveryCode,
          },
        ],
      );
      return;
    }

    const masterKey = await getCachedMasterKey();
    if (!masterKey) {
      Alert.alert(
        'Unlock your vault',
        'Enter your Recovery Code to unlock the vault, then try again.',
        [
          { text: 'Cancel', style: 'cancel' },
          {
            text: 'Unlock',
            // Come back here after the code is accepted, not to Dashboard.
            onPress: () =>
              (navigationRef as any).navigate('Auth', {
                screen: 'UnlockVault',
                params: {
                  returnTo: {
                    root: 'Profile',
                    routes: [{ name: 'Settings' }, { name: 'PendingConnections' }],
                  },
                },
              }),
          },
        ],
      );
      return;
    }

    setCompletingIds(prev => new Set(prev).add(row.relationshipId));
    try {
      const sealed = await sealForRecipient(masterKey, row.wrappingPublicKey);
      await vaultService.storeTrustedPartyWrappedKey({
        trustedPartyUserId: row.trustedPartyUserId,
        role: row.role,
        wrappedMasterKey: sealed,
        wrapVersion: SEAL_VERSION,
      });
      // Optimistically remove the row; refresh is best-effort below.
      setPending(current =>
        current
          ? current.filter(item => item.relationshipId !== row.relationshipId)
          : current,
      );
      loadPending(false);
    } catch (err) {
      logger.error('Failed to complete handshake:', err);
      Alert.alert(
        'Could not complete connection',
        'Something went wrong. Please try again.',
      );
    } finally {
      setCompletingIds(prev => {
        const next = new Set(prev);
        next.delete(row.relationshipId);
        return next;
      });
    }
  };

  const renderName = (row: PendingConnection): string => {
    const name = [row.trustedPartyFirstName, row.trustedPartyLastName]
      .filter(Boolean)
      .join(' ')
      .trim();
    return name || row.trustedPartyEmail || 'Trusted party';
  };

  if (pending === null) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.center}>
          <ActivityIndicator size="large" color="#29BFC0" />
          <Text style={styles.loadingText}>Loading pending connections…</Text>
        </View>
      </SafeAreaView>
    );
  }

  if (pending.length === 0) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.center}>
          <Text style={styles.emptyTitle}>No pending connections</Text>
          <Text style={styles.emptySubtitle}>
            Everyone you've invited has been fully connected. Check back if you
            invite someone new.
          </Text>
          <TouchableOpacity
            style={styles.primaryButton}
            onPress={() => navigation.goBack()}
          >
            <Text style={styles.primaryButtonText}>Back</Text>
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <ScrollView contentContainerStyle={styles.scrollContent}>
        <Text style={styles.title}>Complete your connections</Text>
        <Text style={styles.subtitle}>
          These people accepted your invitation. Complete each connection so
          they can be ready to decrypt your vault at the right time.
        </Text>

        {pending.map(row => {
          const isWorking = completingIds.has(row.relationshipId);
          return (
            <View key={row.relationshipId} style={styles.card}>
              <View style={{ flex: 1 }}>
                <Text style={styles.roleBadge}>{row.role.toUpperCase()}</Text>
                <Text style={styles.name}>{renderName(row)}</Text>
                <Text style={styles.email}>{row.trustedPartyEmail}</Text>
              </View>
              <TouchableOpacity
                style={[
                  styles.completeButton,
                  isWorking && styles.completeButtonDisabled,
                ]}
                onPress={() => handleComplete(row)}
                disabled={isWorking}
              >
                {isWorking ? (
                  <ActivityIndicator size="small" color="#FFFFFF" />
                ) : (
                  <Text style={styles.completeButtonText}>Complete</Text>
                )}
              </TouchableOpacity>
            </View>
          );
        })}
      </ScrollView>
    </SafeAreaView>
  );
};

export default PendingConnectionsScreen;

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#FFFFFF' },
  center: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: ms(24),
  },
  loadingText: {
    marginTop: ms(12),
    color: '#666',
  },
  emptyTitle: {
    fontSize: ms(18, 0.3),
    fontWeight: '700',
    color: '#1C2C4F',
    marginBottom: ms(8),
  },
  emptySubtitle: {
    fontSize: ms(14, 0.3),
    color: '#666',
    textAlign: 'center',
    lineHeight: ms(20, 0.3),
    marginBottom: ms(20),
  },
  scrollContent: {
    padding: ms(20),
  },
  title: {
    fontSize: ms(22, 0.3),
    fontWeight: '700',
    color: '#1C2C4F',
    marginBottom: ms(8),
  },
  subtitle: {
    fontSize: ms(14, 0.3),
    color: '#666',
    lineHeight: ms(20, 0.3),
    marginBottom: ms(20),
  },
  card: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: ms(16),
    borderRadius: ms(12),
    backgroundColor: '#F5F7FB',
    marginBottom: ms(12),
  },
  roleBadge: {
    fontSize: ms(10, 0.3),
    fontWeight: '700',
    color: '#29BFC0',
    letterSpacing: 0.8,
    marginBottom: ms(4),
  },
  name: {
    fontSize: ms(15, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
  },
  email: {
    fontSize: ms(12, 0.3),
    color: '#666',
    marginTop: ms(2),
  },
  completeButton: {
    backgroundColor: '#29BFC0',
    paddingVertical: ms(10),
    paddingHorizontal: ms(18),
    borderRadius: ms(8),
    minWidth: ms(100),
    alignItems: 'center',
    justifyContent: 'center',
  },
  completeButtonDisabled: {
    opacity: 0.6,
  },
  completeButtonText: {
    color: '#FFFFFF',
    fontSize: ms(13, 0.3),
    fontWeight: '600',
  },
  primaryButton: {
    backgroundColor: '#29BFC0',
    paddingVertical: ms(12),
    paddingHorizontal: ms(24),
    borderRadius: ms(8),
    marginTop: ms(8),
  },
  primaryButtonText: {
    color: '#FFFFFF',
    fontSize: ms(14, 0.3),
    fontWeight: '600',
  },
});
